'use client'

import { useState } from 'react'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { alpha } from '@mui/material/styles'
import { Box, Container, Grid, Typography } from '@mui/material'

// Types
import type { HomeCalculatorProps } from '@/types/components/organisms/home'

// Components
const RevealAtom = dynamic(() => import('@/components/atoms/animation/reveal'))
const PrimaryButtonAtom = dynamic(() => import('@/components/atoms/button/primary'))
const SectionHeadingMolecule = dynamic(() => import('@/components/molecules/solution/section-heading'))
const CalculatorFieldMolecule = dynamic(() => import('@/components/molecules/home/calculator-field'))
const CalculatorResultMolecule = dynamic(() => import('@/components/molecules/home/calculator-result'))

// Share of customers lost each month and the usual commission of intermediary platforms
const CHURN_RATE = 0.05
const COMMISSION_RATE = 0.2
const DAYS_PER_MONTH = 30

// Lets owners estimate how much revenue they lose every month to churn and platform commissions
const HomeCalculatorOrganism = (props: HomeCalculatorProps) => {
	// Props
	const { lng, eyebrow, title, subtitle, inputs, units, results, note, cta } = props

	// States
	const [dailyInvoices, setDailyInvoices] = useState(100)
	const [avgTicket, setAvgTicket] = useState(300)
	const [platformShare, setPlatformShare] = useState(30)

	// Variables
	const numberFormat = new Intl.NumberFormat(lng === 'fa' ? 'fa-IR' : 'en-US', { maximumFractionDigits: 1 })
	const format = (value: number) => numberFormat.format(value)

	// Amounts in million tomans; the ticket is entered in thousand tomans
	const monthlySales = (dailyInvoices * avgTicket * DAYS_PER_MONTH) / 1000
	const churnLoss = monthlySales * CHURN_RATE
	const commission = monthlySales * (platformShare / 100) * COMMISSION_RATE
	const total = churnLoss + commission

	return (
		<Container id="calculator" maxWidth="lg" sx={{ py: { xs: 8, md: 14 }, scrollMarginTop: 96 }}>
			<RevealAtom>
				<SectionHeadingMolecule eyebrow={eyebrow} title={title} subtitle={subtitle} />
			</RevealAtom>

			<RevealAtom delay={0.1}>
				<Grid container sx={{ mt: { xs: 5, md: 8 }, borderRadius: { xs: 6, md: 8 }, overflow: 'hidden', boxShadow: '0 40px 80px -40px rgba(28, 43, 54, 0.35)' }}>
					<Grid size={{ xs: 12, md: 6 }} sx={{ bgcolor: 'common.white', p: { xs: 3, md: 6 }, display: 'flex', flexDirection: 'column', gap: 4 }}>
						<CalculatorFieldMolecule
							label={inputs.dailyInvoices}
							valueText={`${format(dailyInvoices)} ${units.invoice}`}
							value={dailyInvoices}
							min={20}
							max={400}
							step={10}
							onChange={setDailyInvoices}
						/>

						<CalculatorFieldMolecule
							label={inputs.avgTicket}
							valueText={`${format(avgTicket)} ${units.thousandToman}`}
							value={avgTicket}
							min={50}
							max={1500}
							step={10}
							onChange={setAvgTicket}
						/>

						<CalculatorFieldMolecule
							label={inputs.platformShare}
							valueText={`${format(platformShare)}${units.percent}`}
							value={platformShare}
							min={0}
							max={100}
							step={5}
							onChange={setPlatformShare}
						/>

						<Typography sx={{ fontSize: 13, lineHeight: 1.9, color: 'text.secondary' }}>{note}</Typography>
					</Grid>

					<Grid
						size={{ xs: 12, md: 6 }}
						sx={{
							p: { xs: 3, md: 6 },
							bgcolor: 'primary.ultradark',
							background: t => `radial-gradient(circle at 100% 0%, ${alpha(t.palette.primary.main, 0.35)}, transparent 55%), ${t.palette.primary.ultradark}`
						}}
					>
						<CalculatorResultMolecule label={results.monthlySales} value={format(monthlySales)} unit={units.millionToman} />
						<CalculatorResultMolecule label={results.churnLoss} value={format(churnLoss)} unit={units.millionToman} />
						<CalculatorResultMolecule label={results.commission} value={format(commission)} unit={units.millionToman} />
						<CalculatorResultMolecule label={results.total} value={format(total)} unit={units.millionToman} total />

						<Box
							sx={{
								mt: 1,
								'& .MuiButton-root': {
									bgcolor: 'secondary.main',
									// Dark text keeps the gold button readable (white on gold fails contrast)
									color: 'text.primary',
									px: 4,
									fontWeight: 500,
									'&:hover': { bgcolor: 'secondary.light' }
								}
							}}
						>
							<Link href={`/${lng}/signup`}>
								<PrimaryButtonAtom text={cta} size="large" />
							</Link>
						</Box>
					</Grid>
				</Grid>
			</RevealAtom>
		</Container>
	)
}

export default HomeCalculatorOrganism
